import type { Message } from '../../../lib/use-chat';

export const CHAT_HOME_BI = { page_name: 'chat_home' as const, page_version: 'v3_chat_home' as const };

// Sentinel the overlay stores in `seededConvRef` after consuming a `?new=1`
// handoff, so it clears the active thread exactly once (not on every render).
export const NEW_THREAD_SEED = '__orion_new_thread__';

// Voice-call/voice-note turns are persisted wrapped in <voice> tags (the
// harness's transcription convention). Strip the wrapper for display — readers
// see the words, the model sees the channel. Canonical helpers live in
// lib/voice-message; re-exported here for the chat-surface call sites.
export { VOICE_WRAPPER, voiceTranscript, stripVoiceTags } from '../../../lib/voice-message';
export { stripLeakedFraming, stripStreamingLeakedFraming } from '../../../lib/leaked-framing';

import { stripVoiceTags, voiceTranscript } from '../../../lib/voice-message';
import { hasAssistantReaction } from '../../../lib/message-reactions';
import { stripLeakedFraming, stripStreamingLeakedFraming } from '../../../lib/leaked-framing';

// The full assistant-body sanitize applied at render: strip the platform's own leaked
// `<metadata>`/`<system>` framing (tags AND content), THEN the `<voice>` transcript wrapper (tags only,
// inner words kept). Mirrors the native `ChatText.sanitizeAssistantMessage` ordering — framing first, so
// a `<voice>` sitting inside a `<system>` block goes with the block instead of surviving as a bare tag.
// `streaming` — pass true on the live render path: it additionally drops an UNCLOSED trailing framing
// open tag (partial block mid-stream) so the fabrication never flashes; settled text (`false`, the
// default — also what `isVisibleMessage` uses) stays exact server parity.
export function sanitizeAssistantBody(text: string, streaming = false): string {
  return stripVoiceTags(streaming ? stripStreamingLeakedFraming(text) : stripLeakedFraming(text));
}

// Which rows belong to the SETTLED transcript — what a reader sees scrolling back.
//
// `ASSISTANT_STEP` — prose the agent wrote in the same step it called a tool — DOES render, live and
// in history alike (decided 2026-08-27, reversing #7461). It always streamed in as an ordinary
// assistant bubble: `messageStart` fires on the first delta, before the model has called anything, and
// only the closing `messageEnd` reveals the block was a step. Hiding it from the settled transcript
// therefore never hid anything — it retroactively deleted prose the reader had already been shown,
// which is what Monday 12878380670 reported.
//
// `THINKING` is the line that did NOT move: a `thinkingMessage` lifted off a tool call is machine
// status authored by the tool, not by the agent, and it stays live-only — it drives the transient
// progress line while the turn runs (see MessagesPanel) and draws nothing once the answer lands. The
// two roles are a real distinction now, not two names for one thing; don't re-collapse them.
//
// The cross-surface contract lives in `common-lib/fixtures/chat-render-contract.json`, which this
// function is tested against alongside its iOS and Android twins.
export function isVisibleMessage(m: Message): boolean {
  // Every agentCard message stays visible — including the non-first members of a
  // consecutive run, which render nothing but must be present for the run fold.
  if (m.agentCard) return true;
  // An at-work card row's own text is a caption ('✦ X is working on Y'); the card is the content, so
  // the row is always visible even though the bubble path would treat it as empty.
  if (m.agentAtWorkCard) return true;
  if (m.milestoneCard) return true;
  if (m.creditGiftCard) return true;
  // The brief wrap-up renders as the MeetingSummaryCard (a card, not a bubble), so it must survive the
  // empty-assistant-row filter below even if its own text is thin — same rule as the other cards.
  if (m.agentMeetingSummary) return true;
  const role = m.role ?? '';
  if (role !== 'USER' && role !== 'ASSISTANT' && role !== 'ASSISTANT_STEP') return false;
  // Hide a SETTLED assistant-side row (reply OR step) that sanitizes to nothing (was only leaked `<metadata>`/`<system>`
  // framing) and carries no non-text content — an empty bubble reads as a broken reply. Mirrors the
  // server's "a reply that was ONLY framing … skip it" drop and the native `hidesEmptyAssistantRow`.
  // Gated on `!m.animate`: a session-born / still-streaming bubble carries `animate` and grows its
  // content per delta (empty for a frame at messageStart), so hiding it would flicker the live reply —
  // only settled / history rows (no `animate`) are ever hidden. (The `Message` type has no streaming
  // flag; `streaming` lives on `ChatState`.)
  if (
    (role === 'ASSISTANT' || role === 'ASSISTANT_STEP') &&
    !m.animate &&
    !m.connectorCard &&
    !m.opportunityCard &&
    !m.outreachPreviewCard &&
    // Belt and braces for the two share-card rows. Both are SUPPOSED to carry prose — the offer's reply
    // introduces the card, and the response row's sentence rides its own `content` precisely so an app
    // that has never heard of the key still shows the reaction — but a row that arrives without it
    // would otherwise be erased here, which is the one failure nobody would see.
    !m.shareOfferCard &&
    !m.shareResponseCard &&
    // A band offer, or the band itself, can be the whole content of a turn — the connect chain reports
    // back and the card IS the report. Both phones keep these two rows for the same reason.
    !m.bandOfferCard &&
    !m.bandCard &&
    !m.goalCard &&
    !m.choices &&
    !m.replyTo &&
    !m.choiceAnswer &&
    !(m.attachments && m.attachments.length) &&
    !(m.reactions && m.reactions.length) &&
    !sanitizeAssistantBody(m.content ?? '').trim()
  ) {
    return false;
  }
  return true;
}

export function lastIndexOf<T>(list: T[], predicate: (item: T) => boolean): number {
  for (let i = list.length - 1; i >= 0; i--) {
    if (predicate(list[i])) return i;
  }
  return -1;
}

// Fallback "the assistant still owes a reply" signal, derived from the message
// tail — keeps the loader up after the SSE drops mid-turn until the reply lands.
// Ported from mobile (features/chat getIsWaitingForAssistant): walk back; the
// turn is incomplete unless the latest non-step message is the final ASSISTANT.
//
// A SPOKEN user turn is the exception, and it has to be: a `<voice>…</voice>` row is a RECORD of what
// the ASR reported, written by `persistVoiceTurn` explicitly so it does NOT trigger the text loop (the
// live model on the call already answered it aloud). So nothing is ever coming, and this signal has no
// timeout — a call whose last transcript was the user's left the web transcript reading "Thinking…"
// permanently. Reported 2026-08-26: a mis-transcribed fragment landed last and the spinner never stood
// down. Detected off the wrapper rather than `metadata.voiceTranscript`, because the wrapper is what
// the client already receives and reads everywhere else (`voiceTranscript` in lib/voice-message).
//
// A typed turn the assistant REACTED to is answered too: a solo `ReactToMessage` is the whole reply and
// writes no ASSISTANT row, so the loader stayed up until a reload (2026-10-04). A reaction that leads
// real work keeps the loader through `streaming` / `remoteTurnActive`, not through this fallback.
// The turn's internal rows are walked past: the legacy messages route returns TOOL_USE / TOOL_RESULT
// rows, so a server-adopted thread ends `USER → TOOL_USE → TOOL_RESULT` after a solo reaction.
const TURN_INTERNAL_ROLES = new Set(['ASSISTANT_STEP', 'THINKING', 'TOOL_USE', 'TOOL_RESULT']);

export function isWaitingForAssistant(messages: Message[]): boolean {
  if (messages.length === 0) return false;
  let roomQuestion = false;
  for (let i = messages.length - 1; i >= 0; i--) {
    const role = messages[i].role;
    if (role === 'ASSISTANT') return false;
    if (role && TURN_INTERNAL_ROLES.has(role)) continue;
    // A question the meeting room put to the agent is answered in the room, not in this chat: no reply is owed.
    if (messages[i].fromMeetingRoom) {
      roomQuestion = true;
      continue;
    }
    // The tail's newest real message: a reply is owed only if the user TYPED it and got no reaction.
    return voiceTranscript(messages[i].content ?? '') === null && !hasAssistantReaction(messages[i]);
  }
  return !roomQuestion;
}

// The shimmering progress line under a running turn: the newest tool status that is still current.
//
// THINKING ONLY. A `thinkingMessage` lifted off a tool call has no bubble of its own, so this line is
// the only place it is ever shown. An `ASSISTANT_STEP` is deliberately NOT a source: since 2026-08-27 a
// step renders as its own bubble (see `isVisibleMessage`), and echoing it here would print the same
// sentence twice — once as the message the reader is looking at, once as status about it.
//
// An assistant-side row is a BOUNDARY, not something to walk past. Status older than the newest thing
// the agent actually said is stale: in `USER → THINKING("Reading A") → ASSISTANT_STEP("now I'll check
// B")`, scanning past the step to reuse "Reading A" would caption a newer bubble with an older action.
// Nothing current to show is a real answer — the caller falls back to the bare loader.
//
// Scoped to the current turn (after the last USER row) so a previous turn's status can never linger.
export function latestTurnStatus(messages: Message[]): string | undefined {
  const turnStart = lastIndexOf(messages, (m) => m.role === 'USER');
  for (let i = messages.length - 1; i > turnStart; i--) {
    const role = messages[i].role;
    if (role === 'ASSISTANT' || role === 'ASSISTANT_STEP') return undefined;
    if (role === 'THINKING') {
      const text = messages[i].content?.trim();
      if (text) return text;
    }
  }
  return undefined;
}

// The newest assistant reply that has said something — the row whose bubble would carry the turn.
// "Said something" is the sanitized body, as `isVisibleMessage` judges it: a row that is only leaked
// framing draws no bubble, so it must not count as a reply either. Keyed by persisted id when known so
// the streamed bubble and its duplexer-pushed twin agree. Used by the connect handoff to tell "the
// reply the card rode in on" from "the reply the connect woke".
export function latestReplyKey(messages: Message[]): string | undefined {
  const i = lastIndexOf(messages, (m) => m.role === 'ASSISTANT' && sanitizeAssistantBody(m.content ?? '').trim().length > 0);
  if (i < 0) return undefined;
  return messages[i].finalId ?? messages[i].id;
}

// The newest assistant-side OUTPUT row — a reply, an intermediate step, or a card-bearing row — keyed by
// persisted id. Unlike `latestReplyKey` this does NOT require sanitized text, so a turn whose output lands
// as a CARD (an `agentCard`/`milestoneCard`/… rides on an `ASSISTANT` row whose `content` is empty) or as an
// `ASSISTANT_STEP` still counts as "the turn produced output". The connect handoff keys its disarm on this:
// a plain reply already flipped `latestReplyKey`, but a card greeting did not, so the "Analyzing your data…"
// placeholder outlived the answer it was bridging to (it rendered below the received card). THINKING rows are
// deliberately excluded — they clear the placeholder text via `realThinkingActive` while the bridge holds
// across the pre-output gap, and counting them here would drop the wait a beat before any answer exists.
export function latestTurnOutputKey(messages: Message[]): string | undefined {
  const i = lastIndexOf(messages, (m) => m.role === 'ASSISTANT' || m.role === 'ASSISTANT_STEP');
  if (i < 0) return undefined;
  return messages[i].finalId ?? messages[i].id;
}

// The `undefined` locale is DELIBERATE and must stay: it resolves to the browser's own locale, which is
// what this app decided a date/time FORMAT should follow — "the user's own chosen format wins"
// (`DashboardView/GreetingDate.tsx`, after an account-region override made a US account read month-first
// for every browser language). A bare clock carries no localized words, so there is nothing for the
// active i18n language to keep it consistent with.
//
// ⚠️ And NO `hour12` option — the LOCALE picks the hour cycle. This used to pass `hour12: true`, which
// made the `undefined` locale moot for the one thing it most needed to decide: every reader got 12-hour,
// so a browser set to English (Israel) saw `8:26 pm` where its own clock reads `20:26`. The natives never
// had this because they ask ICU for the `jmm` skeleton / `.short` time style, which IS the locale's
// choice; passing `hour12` here was web opting out of the same question.
//
// The day divider is the one place that passes the i18n language instead (`dayDividerLabel`) — its label
// combines a date WITH "Today"/"Yesterday", and a single string must not be half in one language. Android
// composes its own (`LocaleConfig.formattingLocale()` = app language + device region) because its
// `Locale.getDefault()` is region-stripped; iOS's `.current` already resolves to exactly that.
export function formatMessageTime(createdDate?: string): string | undefined {
  if (!createdDate) return undefined;
  const date = new Date(createdDate);
  if (Number.isNaN(date.getTime())) return undefined;
  try {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return undefined;
  }
}

export function speakerName(m: Message, myFirstName: string | undefined, agentName: string): string {
  if (m.role === 'USER') return myFirstName || 'You';
  return agentName;
}

// True when `next` is another plain assistant reply from the SAME speaker as `current`, sent
// with nothing from the user in between — a rapid-fire burst rather than normal turn-taking.
// `agentCard`/`agentAtWorkCard`/`milestoneCard`/`creditGiftCard` rows render as their own widgets (never a plain bubble), so they
// always break a run. Un-attributed replies (no `agentId`) are all the same speaker — the
// Maestro — so a burst of plain Maestro turns groups too, not just sub-agent round-table turns.
// Used to tighten the row gap between burst members so several bubbles from one agent read as
// one thought instead of full turn-taking spacing repeated between every bubble (issue: "weird
// spacing when the agent sends multiple messages one after the other").
export function continuesAssistantRun(current: Message, next: Message | undefined): boolean {
  if (!next) return false;
  const isPlainAssistantReply = (m: Message) =>
    // Either assistant-side role: a step bubble followed by the turn's answer is the same speaker
    // twice, so it takes the burst spacing rather than full turn-taking spacing between them.
    (m.role === 'ASSISTANT' || m.role === 'ASSISTANT_STEP') &&
    !m.agentCard &&
    !m.agentAtWorkCard &&
    !m.milestoneCard &&
    !m.creditGiftCard &&
    !m.agentMeetingSummary;
  if (!isPlainAssistantReply(current) || !isPlainAssistantReply(next)) return false;
  return (current.agentId ?? null) === (next.agentId ?? null);
}

export function todayStamp(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Row spacing. A message's TRAILING BLOCK — its reaction chips and the reply/copy/react tool bar —
// hangs under the bubble it belongs to, bound to it by TRAILING_BLOCK_GAP, so the gap AFTER the row
// has to be bigger than that bond. Otherwise the block sits nearer the NEXT message than its own and
// a reader reaches for it to react to that one — which is how a 😂 aimed at one reply was persisted
// on the reply before it (conversation 503028f2-4ed0-48c3-b4cd-c8308d1e2542). Spacing is load-bearing
// here, not cosmetic: it is what says which message a control acts on.
//
// Web-only by construction, not a skipped surface: both phones open the react action by LONG-PRESSING
// the bubble itself (`ChatBubble` -> `ChatActionMenuOverlay`, which shows the message it is acting
// on), so the control is bound to its message by the touch that opened it — there is no free-floating
// tool bar between two messages to misattribute.
export const TRAILING_BLOCK_GAP = 12;
export const BURST_ROW_GAP = 6;
export const TURN_ROW_GAP = 24;

/**
 * The gap below a message row: the tight burst gap only when the row really does end at its bubble,
 * the full turn gap whenever a trailing block has to stay bound to the message above it.
 */
export function rowGapAfter(continuesRun: boolean, hasTrailingBlock: boolean): number {
  return continuesRun && !hasTrailingBlock ? BURST_ROW_GAP : TURN_ROW_GAP;
}
